import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { canonicalCourses } from '@/data/courses';

// Map a UI program href to a canonical course slug, if one exists.
const canonicalCourseSlugFromHref = (href: string): string | null => {
  const match = canonicalCourses.find((c) => c.path === href);
  return match ? match.slug : null;
};

export interface HomepageProgram {
  title: string;
  description: string;
  icon: React.ElementType;
  features: string[];
  href: string;
  badge?: string;
  category: 'selective' | 'foundations' | 'naplan' | 'gate';
}

interface ExpertCoursesHeaderProps {
  subtitle: string;
}

export const ExpertCoursesHeader = ({ subtitle }: ExpertCoursesHeaderProps) => {
  return (
    <div className="text-center space-y-3 mb-8 md:mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-primary font-medium text-xs md:text-sm">
        <Sparkles className="h-3.5 w-3.5 md:h-4 md:w-4" />
        Tailored Learning Paths
      </div>
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
        Expert Courses for Every Student
      </h2>
      <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto">
        {subtitle}
      </p>
    </div>
  );
};

interface MobileCourseCardProps {
  program: HomepageProgram;
  index: number;
}

export const MobileCourseCard = ({ program, index }: MobileCourseCardProps) => {
  return (
    <div
      className="transition-all duration-300"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <Link to={program.href} className="block">
        <article className="relative bg-card/80 backdrop-blur-sm rounded-2xl p-5 border border-border/50 shadow-sm hover:shadow-md transition-all duration-300 active:scale-[0.98]">
          {program.badge && (
            <Badge className="absolute -top-2 right-4 bg-accent text-white text-xs px-3 py-1 rounded-full shadow-md">
              {program.badge}
            </Badge>
          )}
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <program.icon className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-heading font-semibold text-foreground mb-1">
                {program.title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {program.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {program.features.slice(0, 2).map((feature, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 text-xs text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    <CheckCircle className="h-3 w-3" />
                    {feature}
                  </span>
                ))}
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
          </div>
        </article>
      </Link>
    </div>
  );
};

interface MobileCoursesListProps {
  programs: HomepageProgram[];
}

export const MobileCoursesList = ({ programs }: MobileCoursesListProps) => {
  return (
    <div className="md:hidden space-y-4">
      {programs.map((program, index) => (
        <MobileCourseCard key={index} program={program} index={index} />
      ))}
    </div>
  );
};

const categoryContent = {
  selective: { label: 'Selective & OC', description: 'Prepare for competitive entry exams', color: 'from-primary to-primary/70' },
  foundations: { label: 'Foundations', description: 'Build strong academic fundamentals', color: 'from-accent to-accent/70' },
  naplan: { label: 'NAPLAN', description: 'Excel in standardised assessments', color: 'from-emerald-500 to-emerald-400' },
  gate: { label: 'GATE', description: 'Prepare for WA selective entry', color: 'from-primary to-primary/70' },
} as const;

interface DesktopCoursesCarouselProps {
  programs: HomepageProgram[];
  selectedState?: string | null;
}

export const DesktopCoursesCarousel = ({ programs, selectedState = null }: DesktopCoursesCarouselProps) => {
  const [activeCategory, setActiveCategory] = useState('selective');
  
  const categoryOrder: HomepageProgram['category'][] = ['selective', 'gate', 'foundations', 'naplan'];
  const categories = categoryOrder
    .filter((id) => programs.some((program) => program.category === id))
    .map((id) => ({
      ...categoryContent[id],
      id,
      label: id === 'selective' && selectedState === 'VIC'
        ? 'Selective & Scholarship'
        : categoryContent[id].label,
    }));

  useEffect(() => {
    if (!categories.some((category) => category.id === activeCategory)) {
      setActiveCategory(categories[0]?.id ?? 'foundations');
    }
  }, [activeCategory, categories]);
  
  // Get programs for active category - no fallback to avoid showing unrelated programs
  const activePrograms = programs.filter((program) => program.category === activeCategory);
  const displayPrograms = activePrograms;
  const activeCategoryData = categories.find(c => c.id === activeCategory);
  
  // ItemList of Courses for the homepage carousel.
  // Only programs that map to a canonical Course in src/data/courses.ts are
  // emitted. Items reference the canonical Course @id so the full
  // Course definition is emitted exactly once - on its own /programs/* page.
  const courseListItems = programs
    .map((program) => ({ program, slug: canonicalCourseSlugFromHref(program.href) }))
    .filter((entry): entry is { program: HomepageProgram; slug: string } => entry.slug !== null);

  const courseSchemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Schoolola Expert Courses",
    "description": "Comprehensive tutoring courses for Australian students",
    "numberOfItems": courseListItems.length,
    "itemListElement": courseListItems.map(({ program }, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Course",
        "@id": `https://schoolola.com.au${program.href}#course`,
        "url": `https://schoolola.com.au${program.href}`,
        "name": program.title,
      },
    })),
  };
  
  return (
    <>
      {/* SEO Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(courseSchemaData)}
        </script>
      </Helmet>
      
      <div className="hidden md:block relative">
        {/* Decorative background */}
        <div className="absolute -top-20 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 right-1/4 w-72 h-72 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
        
        {/* Split Screen Container */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 min-h-[500px]">
            
            {/* Left Side - Categories */}
            <div className="col-span-4 lg:col-span-3">
              <div className="sticky top-24 space-y-3">
                <nav aria-label="Course categories" className="space-y-2">
                  {categories.map((category) => {
                    const isActive = activeCategory === category.id;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`w-full text-left p-4 rounded-2xl transition-all duration-300 group relative overflow-hidden ${
                          isActive
                            ? 'bg-card shadow-lg border-2 border-primary/30'
                            : 'bg-card/50 hover:bg-card border border-border/50 hover:border-primary/20'
                        }`}
                        aria-selected={isActive}
                        role="tab"
                      >
                        {/* Active indicator line */}
                        <div 
                          className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${category.color} transition-all duration-300 ${
                            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                          }`}
                        />
                        
                        <div className="relative z-10">
                          <h3 className={`font-heading font-semibold text-sm lg:text-base transition-colors ${
                            isActive ? 'text-primary' : 'text-foreground group-hover:text-primary'
                          }`}>
                            {category.label}
                          </h3>
                          <p className="text-xs lg:text-sm text-muted-foreground mt-1 line-clamp-2">
                            {category.description}
                          </p>
                        </div>
                        
                        {/* Arrow indicator */}
                        <ArrowRight 
                          className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-all duration-300 ${
                            isActive 
                              ? 'opacity-100 translate-x-0 text-primary' 
                              : 'opacity-0 -translate-x-2 text-muted-foreground group-hover:opacity-50 group-hover:translate-x-0'
                          }`}
                        />
                      </button>
                    );
                  })}
                </nav>
                
              </div>
            </div>
            
            {/* Right Side - Course Details */}
            <div className="col-span-8 lg:col-span-9">
              {/* Category Header */}
              <div className="mb-6 pb-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${activeCategoryData?.color} flex items-center justify-center shadow-md`}>
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-foreground">
                      {activeCategoryData?.label}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {activeCategoryData?.description}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Course Cards Grid */}
              <div 
                role="tabpanel"
                aria-label={activeCategoryData?.label}
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5"
              >
                {displayPrograms.slice(0, 4).map((program, index) => (
                  <article
                    key={`${activeCategory}-${index}`}
                    className="group relative bg-card/80 backdrop-blur-sm rounded-2xl border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all duration-300 overflow-hidden animate-fade-in"
                    style={{ animationDelay: `${index * 75}ms` }}
                    itemScope
                    itemType="https://schema.org/Course"
                  >
                    {/* Hidden SEO metadata */}
                    <meta itemProp="name" content={program.title} />
                    <meta itemProp="description" content={program.description} />
                    <link itemProp="url" href={`https://schoolola.com.au${program.href}`} />
                    
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-transparent to-accent/0 group-hover:from-primary/5 group-hover:to-accent/5 transition-all duration-300" />
                    
                    {program.badge && (
                      <Badge className="absolute top-4 right-4 bg-accent text-white px-2.5 py-1 z-10 rounded-full text-xs font-medium shadow-md">
                        {program.badge}
                      </Badge>
                    )}
                    
                    <div className="relative p-5 lg:p-6 flex flex-col h-full z-10">
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`h-12 w-12 bg-gradient-to-br ${activeCategoryData?.color} rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0`}>
                          <program.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-lg font-heading font-bold text-foreground group-hover:text-primary transition-colors" itemProp="name">
                            {program.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2" itemProp="description">
                            {program.description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Features */}
                      <ul className="space-y-2 flex-1 mb-4" itemProp="teaches">
                        {program.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <CheckCircle className="h-3 w-3 text-primary" />
                            </div>
                            <span className="text-sm text-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      {/* CTA */}
                      <Link to={program.href} itemProp="url" className="mt-auto" aria-label={`Learn more about ${program.title.replace(/<[^>]*>/g, '')}`}>
                        <Button 
                          variant="ghost" 
                          className="w-full rounded-xl bg-primary/5 text-primary hover:bg-primary hover:text-white transition-all duration-300 group/btn"
                        >
                          Learn More about {program.title.replace(/<[^>]*>/g, '')}
                          <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
              
              {/* Empty state if no programs match */}
              {displayPrograms.length === 0 && (
                <div className="flex items-center justify-center h-64 text-center">
                  <div>
                    <p className="text-muted-foreground mb-4">No courses found in this category.</p>
                    <Link to="/programs">
                      <Button variant="outline" className="rounded-full">
                        Browse All Programs
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
